"use client";

import { Check, Copy, QrCode } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useUserInfo } from "@/hooks/useUserInfo";

interface AccountAssetsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	language: string;
	principal: string;
	items?: Array<{
		key: string;
		label: string;
		value?: string | null;
	}>;
}

export function AccountAssetsModal({
	open,
	onOpenChange,
	language,
	principal,
	items,
}: AccountAssetsModalProps) {
	const { isMobile } = useIsMobile();
	const { userInfo } = useUserInfo({
		principal,
		enabled: open && Boolean(principal),
	});
	const [copiedKey, setCopiedKey] = useState<string | null>(null);
	const [expandedQrKey, setExpandedQrKey] = useState<string | null>("principal");
	const [qrCodeDataUrls, setQrCodeDataUrls] = useState<Record<string, string>>({});
	const displayName =
		userInfo?.username?.trim() ||
		(language === "zh" ? "我的账户" : "My Account");
	const avatarSrc = principal
		? userInfo?.image
			? `https://api.odin.fun/v1/user/${principal}/image`
			: `https://images.odin.fun/user/${principal}`
		: undefined;

	const addressDetailItems = useMemo(() => {
		if (items) {
			return items.filter((item) => item.value);
		}

		return [
			{
				key: "principal",
				label: language === "zh" ? "身份ID" : "Identity ID",
				value: principal || "",
			},
			{
				key: "wallet",
				label: language === "zh" ? "绑定钱包" : "Binding Wallet",
				value: userInfo?.btc_wallet_address || "",
			},
			{
				key: "btc_dep",
				label: language === "zh" ? "BTC充值" : "BTC Deposit",
				value: userInfo?.btc_deposit_address || "",
			},
			{
				key: "rune_dep",
				label: language === "zh" ? "符文充值" : "Rune Deposit",
				value: userInfo?.rune_deposit_address || "",
			},
		].filter((item) => item.value);
	}, [items, language, principal, userInfo]);

	useEffect(() => {
		if (!open) {
			return;
		}

		if (!addressDetailItems.some((item) => item.key === expandedQrKey)) {
			setExpandedQrKey(addressDetailItems[0]?.key || "principal");
		}
	}, [addressDetailItems, expandedQrKey, open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		let isCancelled = false;

		const generateAllQRs = async () => {
			const urls: Record<string, string> = {};

			for (const item of addressDetailItems) {
				if (!item.value) continue;
				try {
					const url = await QRCode.toDataURL(item.value, {
						margin: 2,
						width: 300,
					});
					if (!isCancelled) {
						urls[item.key] = url;
					}
				} catch {}
			}

			if (!isCancelled) {
				setQrCodeDataUrls(urls);
			}
		};

		void generateAllQRs();

		return () => {
			isCancelled = true;
		};
	}, [addressDetailItems, open]);

	const handleCopy = async (value: string, key: string) => {
		if (!value) return;
		await navigator.clipboard.writeText(value);
		setCopiedKey(key);
		window.setTimeout(() => setCopiedKey(null), 2000);
	};

	if (isMobile) {
		return (
			<Drawer
				open={open}
				onOpenChange={(nextOpen) => {
					onOpenChange(nextOpen);
					if (nextOpen) {
						setExpandedQrKey("principal");
					}
				}}
			>
				<DrawerContent showClose>
					<DrawerHeader className="sr-only">
						<DrawerTitle>
							{language === "zh" ? "账户资产" : "Account Assets"}
						</DrawerTitle>
						<DrawerDescription>
							{language === "zh"
								? "查看并复制账户地址信息"
								: "View and copy account address information"}
						</DrawerDescription>
					</DrawerHeader>
					<div className="mx-auto flex w-full max-w-sm flex-col gap-3 p-4 pt-8">
						<div className="mb-1 flex items-center gap-3 px-1">
							<Avatar className="h-11 w-11 rounded-2xl border border-gray-200 bg-gray-100 shadow-sm dark:border-gray-800 dark:bg-gray-900">
								<AvatarImage src={avatarSrc} alt={displayName} />
								<AvatarFallback className="rounded-2xl bg-gray-100 text-sm font-bold text-gray-700 dark:bg-gray-900 dark:text-gray-200">
									{displayName.slice(0, 1).toUpperCase()}
								</AvatarFallback>
							</Avatar>
							<div className="min-w-0">
								<p className="truncate text-base font-bold text-gray-900 dark:text-white">
									{displayName}
								</p>
							</div>
						</div>
						{addressDetailItems.map((item) => {
							const isExpanded = expandedQrKey === item.key;
							return (
								<div
									key={item.key}
									className={`overflow-hidden rounded-2xl border border-gray-100 transition-all dark:border-gray-800 ${
										isExpanded
											? "bg-gray-50 ring-1 ring-light-primary/20 dark:bg-gray-800/40"
											: "bg-transparent"
									}`}
								>
									<div className="flex items-center justify-between p-3">
											<div
												onClick={() => void handleCopy(item.value || "", item.key)}
											className="group flex min-w-0 flex-1 cursor-pointer flex-col"
										>
											<span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 transition-colors group-hover:text-light-primary">
												{item.label}
											</span>
											<div className="mt-0.5 flex items-center gap-1.5 text-xs font-mono break-all text-gray-500">
												<span
													className={
														isExpanded ? "font-bold text-gray-900 dark:text-white" : ""
													}
												>
													{item.value}
												</span>
												<div className="shrink-0 p-1">
													{copiedKey === item.key ? (
														<Check className="h-3 w-3 text-green-500" />
													) : (
														<Copy className="h-3 w-3 text-gray-400 group-hover:text-light-primary" />
													)}
												</div>
											</div>
										</div>
										<button
											type="button"
											onClick={() => setExpandedQrKey(isExpanded ? null : item.key)}
											className={`ml-4 rounded-xl p-2.5 transition-all ${
												isExpanded
													? "bg-light-primary text-white"
													: "bg-gray-100 text-gray-400 dark:bg-gray-800"
											}`}
										>
											<QrCode className="h-6 w-6" />
										</button>
									</div>
									<AnimatePresence>
										{isExpanded && (
											<motion.div
												initial={{ height: 0, opacity: 0 }}
												animate={{ height: "auto", opacity: 1 }}
												exit={{ height: 0, opacity: 0 }}
												className="overflow-hidden border-t border-gray-100 bg-white px-6 py-4 dark:border-gray-800 dark:bg-gray-900/40"
											>
												<div className="flex flex-col items-center">
													<div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
														{qrCodeDataUrls[item.key] ? (
															<img src={qrCodeDataUrls[item.key]} alt="QR" className="h-40 w-40" />
														) : (
															<QrCode className="h-12 w-12 animate-pulse text-gray-300" />
														)}
													</div>
													<p className="mt-3 text-[10px] font-medium text-gray-400">
														{language === "zh"
															? "扫码复制地址"
															: "Scan to copy address"}
													</p>
												</div>
											</motion.div>
										)}
									</AnimatePresence>
								</div>
							);
						})}
					</div>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				onOpenChange(nextOpen);
				if (nextOpen) {
					setExpandedQrKey("principal");
				}
			}}
		>
			<DialogContent className="max-w-3xl overflow-hidden rounded-3xl border-none p-0 shadow-2xl">
				<div className="flex h-[500px]">
						<div className="w-2/5 overflow-y-auto border-r border-gray-100 bg-gray-50/50 p-6 dark:border-gray-800 dark:bg-gray-900/50">
							<div className="mb-6 flex items-center gap-3 px-2">
								<Avatar className="h-11 w-11 rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
									<AvatarImage src={avatarSrc} alt={displayName} />
									<AvatarFallback className="rounded-2xl bg-white text-sm font-bold text-gray-700 dark:bg-gray-900 dark:text-gray-200">
										{displayName.slice(0, 1).toUpperCase()}
									</AvatarFallback>
								</Avatar>
								<div className="min-w-0">
									<p className="truncate text-base font-bold text-gray-900 dark:text-white">
										{displayName}
									</p>
								</div>
							</div>
							<div className="space-y-3">
							{addressDetailItems.map((item) => (
								<div
									key={item.key}
									onClick={() => setExpandedQrKey(item.key)}
									className={`group cursor-pointer rounded-2xl border p-4 transition-all ${
										expandedQrKey === item.key
											? "border-light-primary bg-white shadow-sm ring-1 ring-light-primary/10 dark:bg-gray-800"
											: "border-transparent bg-transparent hover:border-gray-200 hover:bg-white dark:hover:bg-gray-800/50"
									}`}
								>
									<p
										className={`mb-1 text-[10px] font-bold uppercase tracking-wider ${
											expandedQrKey === item.key
												? "text-light-primary"
												: "text-gray-400"
										}`}
									>
										{item.label}
									</p>
									<p
										className={`truncate font-mono text-xs ${
											expandedQrKey === item.key
												? "font-bold text-gray-900 dark:text-white"
												: "text-gray-500"
										}`}
									>
										{item.value}
									</p>
								</div>
							))}
						</div>
					</div>
					<div className="flex flex-1 flex-col items-center justify-center bg-white p-8 text-center dark:bg-dark-surface">
						{expandedQrKey &&
							(() => {
								const item = addressDetailItems.find(
									(detailItem) => detailItem.key === expandedQrKey,
								);
								if (!item) return null;

								return (
									<motion.div
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										key={item.key}
										className="w-full max-w-xs"
									>
										<h2 className="mb-2 text-2xl font-black">{item.label}</h2>
											<button
												type="button"
												onClick={() => void handleCopy(item.value || "", item.key)}
												className="group mb-8 flex w-full items-start gap-3 rounded-3xl bg-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-200 dark:bg-gray-800"
											>
												<span className="min-w-0 flex-1 break-all font-mono text-xs leading-5 text-gray-600 dark:text-gray-300">
													{item.value}
												</span>
												{copiedKey === item.key ? (
													<Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
												) : (
													<Copy className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 group-hover:text-light-primary" />
												)}
											</button>
										<div className="mx-auto w-fit rounded-[2rem] border border-gray-100 bg-white p-6 shadow-xl">
											{qrCodeDataUrls[item.key] ? (
												<img
													src={qrCodeDataUrls[item.key]}
													alt="QR"
													className="h-40 w-48 object-contain"
												/>
											) : (
												<QrCode className="h-12 w-12 animate-pulse text-gray-300" />
											)}
										</div>
										<p className="mt-6 text-sm font-medium text-gray-400">
											{language === "zh"
												? "扫描二维码复制地址"
												: "Scan QR code to copy address"}
										</p>
									</motion.div>
								);
							})()}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
